import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';
import './Dashboard.css';

export default function OverviewDashboard({ departments }) {
  const [recentSubmissions, setRecentSubmissions] = useState([]);
  const [analytics, setAnalytics] = useState({
    totalSubmissions: 0,
    submissionsByDepartment: {},
    completionRates: {},
  });

  useEffect(() => {
    const fetchOverviewData = async () => {
      try {
        // Fetch recent submissions across all departments
        const formsRef = collection(db, 'forms');
        const recentQuery = query(
          formsRef,
          orderBy('submittedAt', 'desc'),
          limit(20)
        );
        
        const formsSnapshot = await getDocs(recentQuery);
        const submissions = formsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setRecentSubmissions(submissions);

        // Calculate analytics for each department
        const analytics = {
          totalSubmissions: submissions.length,
          submissionsByDepartment: {},
          completionRates: {}
        };

        // Add admin location
        const adminLocationId = '27gQp4BHDD8lHpESiyed';
        const adminSubmissions = submissions.filter(s => s.locationId === adminLocationId);
        analytics.submissionsByDepartment['admin'] = adminSubmissions.length;
        analytics.completionRates['admin'] = adminSubmissions.length > 0
          ? (adminSubmissions.filter(s => s.status === 'submitted').length / adminSubmissions.length) * 100
          : 0;

        // Process other departments
        departments.forEach(dept => {
          const deptId = dept.name.toLowerCase().replace(/\s+/g, '-');
          if (deptId === 'admin') return; // Skip admin as it's already handled

          const deptSubmissions = submissions.filter(s => 
            s.locationId === dept.id || 
            s.assignedLocations?.some(loc => loc.id === dept.id)
          );
          
          analytics.submissionsByDepartment[deptId] = deptSubmissions.length;
          analytics.completionRates[deptId] = deptSubmissions.length > 0
            ? (deptSubmissions.filter(s => s.status === 'submitted').length / deptSubmissions.length) * 100
            : 0;
        });

        setAnalytics(analytics);
      } catch (error) {
        console.error('Error fetching overview data:', error);
      }
    };

    fetchOverviewData();
  }, [departments]);

  return (
    <div className="overview-dashboard">
      <div className="analytics-section">
        <h2>Overview Analytics</h2>
        <div className="analytics-grid">
          <div className="analytics-card">
            <h3>Total Submissions</h3>
            <p>{analytics.totalSubmissions}</p>
          </div>
          {/* Admin card first */}
          <div className="analytics-card">
            <h3>Admin Submissions</h3>
            <p>{analytics.submissionsByDepartment['admin'] || 0}</p>
            <p className="completion-rate">
              Completion Rate: {(analytics.completionRates['admin'] || 0).toFixed(1)}%
            </p>
          </div>
          {/* Other department cards */}
          {departments.map(dept => {
            const deptId = dept.name.toLowerCase().replace(/\s+/g, '-');
            if (deptId === 'admin') return null; // Skip admin as it's shown above
            return (
              <div key={dept.id} className="analytics-card">
                <h3>{dept.name} Submissions</h3>
                <p>{analytics.submissionsByDepartment[deptId] || 0}</p>
                <p className="completion-rate">
                  Completion Rate: {(analytics.completionRates[deptId] || 0).toFixed(1)}%
                </p>
              </div>
            );
          })}
        </div>
      </div>

      <div className="recent-submissions-section">
        <h2>Recent Submissions</h2>
        <div className="submissions-list">
          {recentSubmissions.map(submission => {
            // Handle admin location
            if (submission.locationId === '27gQp4BHDD8lHpESiyed') {
              return (
                <div key={submission.id} className="submission-card">
                  <h3>{submission.templateName || 'Untitled Form'}</h3>
                  <p>Department: Admin</p>
                  <p>Submitted: {new Date(submission.submittedAt?.toDate()).toLocaleString()}</p>
                  <p>Status: {submission.status}</p>
                </div>
              );
            }

            // Handle other departments
            const dept = departments.find(d => d.id === submission.locationId);
            return (
              <div key={submission.id} className="submission-card">
                <h3>{submission.templateName || 'Untitled Form'}</h3>
                <p>Department: {dept ? dept.name : 'Unknown'}</p>
                <p>Submitted: {new Date(submission.submittedAt?.toDate()).toLocaleString()}</p>
                <p>Status: {submission.status}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
